import "core-js/modules/es6.object.define-property";
import _isEmpty from "lodash/isEmpty";

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import React from 'react';
import PropTypes from 'prop-types';
import classes from '@nerdwallet/base-styles/classes';
import { getCreditScore } from '@nerdwallet/js-auth-tools/selectors';
import { getApprovalOdds, getLegacyId, getMaximumFICO, getMinimumFICO } from '@nerdwallet/marketplace/selectors/credit-cards';
import { setTrk, TrkTag, TrkElement, TrkPageType } from '@nerdwallet/nw-client-lib/trk-helper';
import { ElementInteraction } from '@nerdwallet/nw-client-lib/tracking';
import { Button, Link, Title, Tooltip, Type } from '@nerdwallet/currency';
import { calculateBucket, getCreditScoreComparisonContent } from '@nerdwallet/likelihood-of-approval';
import { handleAuthClickDefault } from '../likelihood-of-approval-cta';
import styles from '../marketplace-product-card.module.less';

var matchScoreTextWithCTA = function matchScoreTextWithCTA(url) {
  return /*#__PURE__*/React.createElement(React.Fragment, null, "To calculate your NerdWallet match score, we analyzed your preferences, credit score, and the cards already in your wallet. ", /*#__PURE__*/React.createElement(Link, {
    href: url
  }, "Edit answers"));
};

var MATCH_SCORE_INFO_TEXT = /*#__PURE__*/React.createElement(Type, null, "What do lenders look for? Credit score among other factors. But heads up, they may not use the TransUnion Vantage Score 3.0 like we do, and these odds don't guarantee approval or denial.");

var unlockedWithTUandApprovalOdds = function unlockedWithTUandApprovalOdds(approvalOdds, url) {
  return /*#__PURE__*/React.createElement(Type, {
    className: classes('color-neutral-darkest')
  }, matchScoreTextWithCTA(url), /*#__PURE__*/React.createElement(Title, {
    size: "x-small",
    className: classes('margin-bottom-3', 'margin-top-3')
  }, "".concat(calculateBucket(approvalOdds).percentile / 10, "\n      in 10 Nerdwallet members (with credit like yours) were approved for this card.")), MATCH_SCORE_INFO_TEXT);
};

var unlockedWithTU = function unlockedWithTU(creditScore, marketplaceEntity, url) {
  var creditScoreContent = getCreditScoreComparisonContent(creditScore, getMaximumFICO(marketplaceEntity), getMinimumFICO(marketplaceEntity));
  return /*#__PURE__*/React.createElement(Type, {
    className: classes('color-neutral-darkest')
  }, matchScoreTextWithCTA(url), /*#__PURE__*/React.createElement(Title, {
    size: "x-small",
    className: classes('margin-bottom-3', 'margin-top-3')
  }, "You're ".concat(creditScoreContent.pointDifference, " ").concat(creditScoreContent.relativeScoreLabel, " suggested credit score of ").concat(creditScoreContent.scoreToCompare, ".")), "But, heads up, being within the recommended range doesn't guarantee approval. Credit score is only one factor lenders consider and they may not use the TransUnion Vantage Score 3.0 like we do.");
};

var unlockedWithoutTU = function unlockedWithoutTU(handleAuthCTAClick, legacyId, url) {
  return /*#__PURE__*/React.createElement(Type, {
    className: classes('color-neutral-darkest')
  }, matchScoreTextWithCTA(url), /*#__PURE__*/React.createElement(Title, {
    size: "x-small",
    className: classes('margin-top-3', 'margin-bottom-3')
  }, "Your NerdWallet match score could be more accurate with your exact credit score."), MATCH_SCORE_INFO_TEXT, /*#__PURE__*/React.createElement(Button, {
    onClick: function onClick() {
      return handleAuthCTAClick(legacyId);
    },
    className: styles['match-score-get-cs-cta']
  }, "GET MY CREDIT SCORE"));
};

var MatchScoreToolTip = function MatchScoreToolTip(_ref) {
  var _params;

  var analytics = _ref.analytics,
      identity = _ref.identity,
      marketplaceEntity = _ref.marketplaceEntity,
      handleAuthCTAClick = _ref.handleAuthCTAClick,
      taxonomy = _ref.taxonomy;
  var legacyId = getLegacyId(marketplaceEntity);
  var url = setTrk({
    url: "/recommend?askCreditScore=true&matchScoreCTACardId=".concat(legacyId),
    taxonomy: taxonomy,
    params: (_params = {}, _defineProperty(_params, TrkTag.element, TrkElement.hyperlink), _defineProperty(_params, TrkTag.location, "match-score-tooltip-popover--prodId".concat(legacyId)), _defineProperty(_params, TrkTag.pageType, TrkPageType.marketPlace), _params)
  });
  var popoverContent;
  var creditScore = getCreditScore(identity);
  var approvalOdds = getApprovalOdds(marketplaceEntity);

  if (creditScore && approvalOdds) {
    popoverContent = unlockedWithTUandApprovalOdds(approvalOdds, url);
  } else if (creditScore) {
    popoverContent = unlockedWithTU(creditScore, marketplaceEntity, url);
  } else {
    popoverContent = unlockedWithoutTU(handleAuthCTAClick, legacyId, url);
  }

  return /*#__PURE__*/React.createElement(ElementInteraction, {
    id: "match-score-tooltip-prodId".concat(legacyId),
    elementName: "Product Card Match Score Tooltip",
    type: "HOVER"
  }, /*#__PURE__*/React.createElement(Tooltip, {
    analytics: !_isEmpty(analytics) ? {
      entityName: (analytics === null || analytics === void 0 ? void 0 : analytics.entityName) || "product_card_match_score_tooltip_".concat(legacyId)
    } : analytics,
    id: "ex1",
    title: "How is my match score calculated?",
    content: /*#__PURE__*/React.createElement("div", {
      className: styles.matchScoreTooltipPopoverContent
    }, popoverContent),
    className: classes('color-neutral-darkest', 'type-2', 'margin-top-0')
  }));
};

MatchScoreToolTip.propTypes = {
  analytics: PropTypes.shape({
    entityName: PropTypes.string
  }),
  identity: PropTypes.object.isRequired,
  // eslint-disable-line react/forbid-prop-types
  marketplaceEntity: PropTypes.object.isRequired,
  // eslint-disable-line react/forbid-prop-types
  handleAuthCTAClick: PropTypes.func,
  taxonomy: PropTypes.object.isRequired // eslint-disable-line react/forbid-prop-types

};
MatchScoreToolTip.defaultProps = {
  analytics: {},
  handleAuthCTAClick: function handleAuthCTAClick(legacyId) {
    return handleAuthClickDefault({
      trk_element: 'CC Match Score Tooltip',
      trk_location: "match-score-tooltip--prodId".concat(legacyId)
    });
  }
};
export default MatchScoreToolTip;